import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { supabase, supabaseReady } from "../lib/supabase";
import { generateBabyId, lastUsedBabyId } from "../lib/babyId";
import { useAuth } from "../lib/AuthContext";
import { useT } from "../lib/LocaleContext";

export function CreateBaby() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const t = useT();
  const [name, setName] = useState("");
  const [creatorName, setCreatorName] = useState("");
  const [birthday, setBirthday] = useState(() => new Date().toISOString().slice(0, 10));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!supabaseReady) {
      setError(t.createBaby.storageError);
      return;
    }
    setSubmitting(true);
    setError(null);

    const id = generateBabyId(16);
    // create_baby (SECURITY DEFINER) inserts the baby + the creator's
    // caretaker row atomically — see migration 0004.
    const { error: rpcError } = await supabase.rpc("create_baby", {
      p_id: id,
      p_name: name.trim(),
      p_birthday: birthday,
      p_display_name: creatorName.trim(),
    });

    if (rpcError) {
      setError(rpcError.message);
      setSubmitting(false);
      return;
    }

    lastUsedBabyId.set(user?.id, id);
    navigate(`/b/${id}`, { replace: true });
  }

  return (
    <main className="screen screen--center">
      <header className="hero">
        <div className="hero__emoji" aria-hidden="true">👶</div>
        <h1>{t.createBaby.title}</h1>
        <p className="hero__sub">{t.createBaby.subtitle}</p>
      </header>

      <form className="form" onSubmit={handleSubmit}>
        <label className="field">
          <span className="field__label">{t.createBaby.nameLabel}</span>
          <input
            className="field__input"
            type="text"
            required
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t.createBaby.namePlaceholder}
            autoCapitalize="words"
          />
        </label>

        <label className="field">
          <span className="field__label">{t.createBaby.birthdayLabel}</span>
          <input
            className="field__input"
            type="date"
            required
            value={birthday}
            onChange={(e) => setBirthday(e.target.value)}
            max={new Date().toISOString().slice(0, 10)}
          />
          <span className="field__hint">{t.createBaby.birthdayHint}</span>
        </label>

        <label className="field">
          <span className="field__label">{t.createBaby.yourNameLabel}</span>
          <input
            className="field__input"
            type="text"
            value={creatorName}
            onChange={(e) => setCreatorName(e.target.value)}
            placeholder={t.createBaby.yourNamePlaceholder}
            autoCapitalize="words"
          />
          <span className="field__hint">{t.createBaby.yourNameHint}</span>
        </label>

        {error && <p className="error" role="alert">{error}</p>}

        <button
          className="btn btn--primary btn--block"
          type="submit"
          disabled={submitting || !name.trim()}
        >
          {submitting ? t.createBaby.submitting : t.createBaby.submit}
        </button>
      </form>
    </main>
  );
}
